import React, { useState, useEffect, useRef } from 'react'
import { usePost, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { Form, InputGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBarcodeRead } from '@fortawesome/pro-duotone-svg-icons'
import { ViewToggle, ViewToggleBtn } from '../styles'
import { resolveLocationByBarcode } from '../utils'

type MoveMode = 'location' | 'nest'

export const ContainerMovePage = () => {
  const {
    state: { csrf },
  } = globalState()

  const [mode, setMode] = useState<MoveMode>('location')

  // ── Move to Location fields ──
  const [lpn, setLpn] = useState('')
  const [destination, setDestination] = useState('')
  const [comment, setComment] = useState('')

  // ── Nest into Container fields ──
  const [childLpn, setChildLpn] = useState('')
  const [parentLpn, setParentLpn] = useState('')

  const [submitting, setSubmitting] = useState(false)

  // Refs for auto-focus
  const lpnRef = useRef<HTMLInputElement | null>(null)
  const destinationRef = useRef<HTMLInputElement | null>(null)
  const childLpnRef = useRef<HTMLInputElement | null>(null)
  const parentLpnRef = useRef<HTMLInputElement | null>(null)

  // Auto-focus first field on mount and mode switch
  useEffect(() => {
    if (mode === 'location') {
      lpnRef.current?.focus()
      lpnRef.current?.select()
    } else {
      childLpnRef.current?.focus()
      childLpnRef.current?.select()
    }
  }, [mode])

  const handleMoveToLocation = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!lpn || !destination) return
    setSubmitting(true)
    try {
      const locationPk = await resolveLocationByBarcode(destination)
      if (!locationPk) {
        notify({ type: 'error', message: `Location "${destination}" not found` })
        return
      }
      const body: Record<string, string | number> = { to_location: locationPk }
      if (comment) body.comment = comment
      const reload = false
      const stringify = true
      const sendResponse = false
      const response = await usePost(
        `/api/inventory/logistic-unit/${lpn}/move/`,
        body,
        csrf,
        reload,
        stringify,
        sendResponse
      )
      if (!response) return
      if (!response.error) {
        notify({ type: 'success', message: `Container ${lpn} moved to ${destination}` })
        setLpn('')
        setDestination('')
        setComment('')
        lpnRef.current?.focus()
        lpnRef.current?.select()
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleNest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!childLpn || !parentLpn) return
    setSubmitting(true)
    try {
      const reload = false
      const stringify = true
      const sendResponse = false
      const response = await usePost(
        `/api/inventory/logistic-unit/${childLpn}/nest/`,
        { parent_lpn: parentLpn },
        csrf,
        reload,
        stringify,
        sendResponse
      )
      if (!response) return
      if (!response.error) {
        notify({ type: 'success', message: `Container ${childLpn} nested into ${parentLpn}` })
        setChildLpn('')
        setParentLpn('')
        childLpnRef.current?.focus()
        childLpnRef.current?.select()
      }
    } finally {
      setSubmitting(false)
    }
  }

  const switchMode = (newMode: MoveMode) => {
    setMode(newMode)
    // Clear fields on mode switch
    setLpn('')
    setDestination('')
    setComment('')
    setChildLpn('')
    setParentLpn('')
  }

  const inputGroupIcon = (
    <InputGroup.Text style={{ width: 40, display: 'flex', justifyContent: 'center', borderRadius: '0 5px 5px 0' }}>
      <FontAwesomeIcon icon={faBarcodeRead} />
    </InputGroup.Text>
  )

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>Container Move</h3>
        </GlobalStyles.CardTitle>
      </GlobalStyles.CardHeader>

      <div style={{ width: 500, maxWidth: '100%', margin: '2em auto', padding: '0 1rem' }}>
        {/* Mode toggle */}
        <div style={{ marginBottom: '2em', display: 'flex', justifyContent: 'center' }}>
          <ViewToggle>
            <ViewToggleBtn $active={mode === 'location'} onClick={() => switchMode('location')}>
              Move to Location
            </ViewToggleBtn>
            <ViewToggleBtn $active={mode === 'nest'} onClick={() => switchMode('nest')}>
              Nest into Container
            </ViewToggleBtn>
          </ViewToggle>
        </div>

        {/* ── Move to Location ── */}
        {mode === 'location' && (
          <Form noValidate onSubmit={handleMoveToLocation}>
            <Form.Group className="required">
              <Form.Label>Container LPN</Form.Label>
              <InputGroup>
                <Form.Control
                  required
                  type="text"
                  placeholder="Scan container LPN"
                  value={lpn}
                  maxLength={100}
                  onChange={(e) => setLpn(e.target.value)}
                  ref={lpnRef}
                />
                {inputGroupIcon}
              </InputGroup>
            </Form.Group>
            <Form.Group className="required" style={{ marginTop: '1.5em' }}>
              <Form.Label>Destination Location</Form.Label>
              <InputGroup>
                <Form.Control
                  required
                  type="text"
                  placeholder="Scan or enter location barcode"
                  value={destination}
                  maxLength={100}
                  onChange={(e) => setDestination(e.target.value)}
                  ref={destinationRef}
                />
                {inputGroupIcon}
              </InputGroup>
            </Form.Group>
            <Form.Group style={{ marginTop: '1.5em' }}>
              <Form.Label>Comment (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Reason for move"
              />
            </Form.Group>
            <GlobalStyles.Button
              className="royal"
              style={{ marginTop: '2em', float: 'right', minWidth: 125 }}
              disabled={!lpn || !destination || submitting}
            >
              {submitting ? 'Moving...' : 'Move Container'}
            </GlobalStyles.Button>
          </Form>
        )}

        {/* ── Nest into Container ── */}
        {mode === 'nest' && (
          <Form noValidate onSubmit={handleNest}>
            <Form.Group className="required">
              <Form.Label>Child Container LPN</Form.Label>
              <InputGroup>
                <Form.Control
                  required
                  type="text"
                  placeholder="Scan the container to nest"
                  value={childLpn}
                  maxLength={100}
                  onChange={(e) => setChildLpn(e.target.value)}
                  ref={childLpnRef}
                />
                {inputGroupIcon}
              </InputGroup>
            </Form.Group>
            <Form.Group className="required" style={{ marginTop: '1.5em' }}>
              <Form.Label>Parent Container LPN</Form.Label>
              <InputGroup>
                <Form.Control
                  required
                  type="text"
                  placeholder="Scan the parent container"
                  value={parentLpn}
                  maxLength={100}
                  onChange={(e) => setParentLpn(e.target.value)}
                  ref={parentLpnRef}
                />
                {inputGroupIcon}
              </InputGroup>
            </Form.Group>
            <GlobalStyles.Button
              className="royal"
              style={{ marginTop: '2em', float: 'right', minWidth: 125 }}
              disabled={!childLpn || !parentLpn || submitting}
            >
              {submitting ? 'Nesting...' : 'Nest Container'}
            </GlobalStyles.Button>
          </Form>
        )}
      </div>
    </GlobalStyles.FullPageCard>
  )
}
